import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CustomerShell } from "@/components/CustomerShell";
import { ProShell } from "@/components/ProShell";
import { whatsappLink, WHATSAPP_NUMBER } from "@/lib/constants";
import { fmtTs } from "@/components/Timeline";
import { toast } from "sonner";
import { ChevronLeft, Phone, MessageCircle, Headphones, Loader2, Send, ChevronDown } from "lucide-react";

const TOPICS = {
    customer: ["Booking / scheduling", "Payment or refund", "Professional behaviour", "Work quality / CareBack", "Invoice or GST", "App problem", "Other"],
    professional: ["Job assignment", "Payout / earnings", "Customer issue at site", "Documents / verification", "App problem", "Other"],
};
const TONE = { OPEN: "bg-amber-50 text-amber-700", IN_PROGRESS: "bg-sky-50 text-sky-700", RESOLVED: "bg-emerald-50 text-emerald-700", CLOSED: "bg-navy/5 text-navy/50" };

export default function Helpdesk() {
    const nav = useNavigate();
    const { user } = useAuth();
    const role = user?.role === "professional" ? "professional" : "customer";
    const Shell = role === "professional" ? ProShell : CustomerShell;
    const [tickets, setTickets] = useState(null);
    const load = () => api.get("/support/tickets/mine").then((r) => setTickets(r.data)).catch(() => setTickets([]));
    useEffect(() => { load(); }, []);
    const tel = `tel:+${WHATSAPP_NUMBER}`;
    const wa = whatsappLink(role === "professional" ? `Hi HOMIIE Care, I'm a HOMIIE Professional (${user?.name || ""}) and need help.` : `Hi HOMIIE Care, I need help with my booking.`);
    return (
        <Shell>
            <div className="px-5 pb-28 pt-6">
                <button data-testid="help-back-btn" onClick={() => nav(-1)} className="mb-3 flex items-center gap-1 text-sm font-semibold text-navy/55"><ChevronLeft size={16} /> Back</button>
                <h1 className="flex items-center gap-2 font-heading text-2xl font-extrabold text-navy"><Headphones className="text-teal" /> Helpdesk</h1>
                <p className="text-sm text-navy/55">{role === "professional" ? "Professional support" : "Customer care"} · 9 AM – 8 PM, every day · HOMIIE Care emergencies 24/7 · support@homiie.in</p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                    <a data-testid="help-call-btn" href={tel} className="flex flex-col items-center gap-1 rounded-2xl bg-navy p-4 text-white active:scale-95"><Phone size={22} /><span className="text-sm font-bold">Call us</span><span className="text-[11px] text-white/60">+91 88583 88788</span></a>
                    <a data-testid="help-whatsapp-btn" href={wa} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-1 rounded-2xl bg-emerald-500 p-4 text-white active:scale-95"><MessageCircle size={22} /><span className="text-sm font-bold">WhatsApp</span><span className="text-[11px] text-white/80">Quick replies</span></a>
                </div>
                <NewTicket role={role} onCreated={load} />
                <h2 className="mt-6 font-heading text-base font-bold text-navy">My tickets</h2>
                {tickets === null && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-orange" /></div>}
                {tickets?.length === 0 && <p data-testid="help-no-tickets" className="mt-2 text-sm text-navy/45">No tickets yet.</p>}
                <div className="mt-2 space-y-3">{tickets?.map((t) => <TicketCard key={t.id} t={t} onChanged={load} />)}</div>
            </div>
        </Shell>
    );
}

function NewTicket({ role, onCreated }) {
    const [topic, setTopic] = useState("");
    const [booking, setBooking] = useState("");
    const [msg, setMsg] = useState("");
    const [bookings, setBookings] = useState([]);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get(role === "professional" ? "/pro/jobs" : "/bookings").then((r) => setBookings((r.data || []).slice(0, 20))).catch(() => {}); }, [role]);
    const submit = async () => {
        setBusy(true);
        try { const { data } = await api.post("/support/tickets", { topic, message: msg, booking_id: booking || undefined }); toast.success(`Ticket ${data.ticket_id} raised — we'll reply here`); setTopic(""); setMsg(""); setBooking(""); onCreated(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    const inp = "w-full rounded-xl border border-navy/15 bg-white px-3 py-2.5 text-sm outline-none focus:border-teal";
    return (
        <div className="mt-5 rounded-2xl border border-navy/10 bg-white p-4" data-testid="help-new-ticket">
            <p className="font-heading font-bold text-navy">What do you need help with?</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
                {TOPICS[role].map((t) => <button key={t} data-testid={`help-topic-${t.replace(/[^a-z]/gi, "").toLowerCase()}`} onClick={() => setTopic(t)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${topic === t ? "border-teal bg-teal text-white" : "border-navy/15 text-navy/70"}`}>{t}</button>)}
            </div>
            {topic && (
                <div className="mt-3 space-y-2">
                    <select data-testid="help-booking-select" value={booking} onChange={(e) => setBooking(e.target.value)} className={inp}>
                        <option value="">Related booking (optional)</option>
                        {bookings.map((b) => <option key={b.id} value={b.id}>{b.booking_id} · {b.service_name}</option>)}
                    </select>
                    <textarea data-testid="help-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Describe the problem…" className={inp} />
                    <button data-testid="help-submit-btn" disabled={busy || msg.trim().length < 5} onClick={submit} className="w-full rounded-full bg-teal py-3 text-sm font-bold text-white disabled:opacity-50">RAISE TICKET</button>
                </div>
            )}
        </div>
    );
}

function TicketCard({ t, onChanged }) {
    const [open, setOpen] = useState(false);
    const [reply, setReply] = useState("");
    const send = async () => {
        try { await api.post(`/support/tickets/${t.id}/reply`, { message: reply }); setReply(""); onChanged(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    return (
        <div data-testid={`ticket-${t.ticket_id}`} className="rounded-2xl border border-navy/10 bg-white">
            <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 p-4 text-left">
                <div className="flex-1"><p className="font-heading text-sm font-bold text-navy">{t.topic}</p><p className="text-[11px] text-navy/45">{t.ticket_id}{t.booking_ref ? ` · ${t.booking_ref}` : ""} · {fmtTs(t.updated_at)}</p></div>
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${TONE[t.status]}`} data-testid={`ticket-status-${t.ticket_id}`}>{t.status.replace("_", " ")}</span>
                <ChevronDown size={16} className={`text-navy/40 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="space-y-2 border-t border-navy/10 p-3">
                    {t.messages.map((m, i) => (
                        <div key={i} className={`rounded-xl p-2.5 text-sm ${m.from === "admin" ? "bg-teal/5" : "bg-navy/[0.03]"}`}>
                            <p className="text-[11px] font-bold text-navy/55">{m.from === "admin" ? "HOMIIE Care" : "You"} · {fmtTs(m.at)}</p>
                            <p className="whitespace-pre-wrap text-navy">{m.text}</p>
                        </div>
                    ))}
                    <div className="flex gap-2">
                        <input data-testid={`ticket-reply-${t.ticket_id}`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Add a reply…" className="flex-1 rounded-full border border-navy/15 px-4 py-2 text-sm outline-none focus:border-teal" />
                        <button data-testid={`ticket-reply-send-${t.ticket_id}`} disabled={!reply.trim()} onClick={send} className="rounded-full bg-teal p-2.5 text-white disabled:opacity-50"><Send size={15} /></button>
                    </div>
                </div>
            )}
        </div>
    );
}
